<template>
  <div class="debt-installment-bill-table">
    <b-table-simple
      bordered
      hover
      class="debt-installment-bill-table__table tbody-hover"
      role="table"
      :aria-label="tableAriaLabel"
    >
      <!-- Table Header -->
      <b-thead>
        <b-tr role="row">
          <b-th
            class="debt-installment-bill-table__header text-center tdDate"
            role="columnheader"
          >
            Ngày
          </b-th>
          <b-th
            class="debt-installment-bill-table__header text-center tdStore"
            role="columnheader"
          >
            Cửa hàng
          </b-th>
          <b-th
            class="debt-installment-bill-table__header text-center tdCustomer"
            role="columnheader"
          >
            Khách hàng
          </b-th>
          <b-th
            class="debt-installment-bill-table__header text-center tdProductName"
            role="columnheader"
          >
            Dịch vụ trả góp
          </b-th>
          <b-th
            class="debt-installment-bill-table__header text-center tdProductPrice"
            role="columnheader"
          >
            Mã hợp đồng
          </b-th>
          <b-th
            class="debt-installment-bill-table__header text-center tdProductPrice"
            role="columnheader"
          >
            Tiền trả góp
          </b-th>
          <b-th
            class="debt-installment-bill-table__header text-center tdTotalPayment"
            role="columnheader"
          >
            Đã thanh toán
          </b-th>
          <b-th
            class="debt-installment-bill-table__header text-center tdTotalPayment"
            role="columnheader"
          >
            Còn lại
          </b-th>
          <b-th
            class="debt-installment-bill-table__header text-center tdTotalAmount"
            role="columnheader"
          >
            Tổng tiền hoá đơn
          </b-th>
          <b-th
            class="debt-installment-bill-table__header text-center"
            role="columnheader"
          >
            Thao tác
          </b-th>
        </b-tr>
      </b-thead>

      <!-- Table Body -->
      <b-tbody
        v-for="bill in data"
        :key="bill.id"
      >
        <b-tr
          v-for="(item, index) in bill.payments"
          :key="index"
          role="row"
        >
          <!-- Date and Bill Number -->
          <b-td
            :rowspan="bill.payments.length + 1"
            class="debt-installment-bill-table__cell tdDate"
            v-show="isShowRow(bill.payments, item)"
            role="cell"
          >
            <div :aria-label="`Ngày tạo: ${formatDate(bill.createdAt)}`">
              {{ formatDate(bill.createdAt) }}
            </div>
            <div>
              <router-link
                :to="{
                  path: '/bills/update-bill',
                  query: { id: bill.billNumber },
                }"
                target="_blank"
                class="debt-installment-bill-table__bill-link"
                :aria-label="`Xem chi tiết hóa đơn ${bill.billNumber}`"
              >
                {{ bill.billNumber }}
              </router-link>
            </div>
          </b-td>

          <!-- Store Name -->
          <b-td
            :rowspan="bill.payments.length + 1"
            class="debt-installment-bill-table__cell text-left tdStore"
            v-show="isShowRow(bill.payments, item)"
            role="cell"
            :aria-label="`Cửa hàng: ${bill.storeName}`"
          >
            <div class="debt-installment-bill-table__store-cell">
              <span>{{ bill.storeName }}</span>
              <span
                v-if="bill.companyName"
                class="badge badge-pill debt-installment-bill-table__company-badge mt-1"
              >
                {{ bill.companyName }}
              </span>
            </div>
          </b-td>

          <!-- Customer Info -->
          <b-td
            :rowspan="bill.payments.length + 1"
            class="debt-installment-bill-table__cell text-left tdCenter tdCustomer"
            v-show="isShowRow(bill.payments, item)"
            role="cell"
          >
            <div class="debt-installment-bill-table__customer-info">
              <b :aria-label="`Tên khách hàng: ${bill.customerName}`">
                {{ bill.customerName }}
              </b>
              <p :aria-label="`Số điện thoại: ${bill.customerMobile}`">
                {{ bill.customerMobile }}
              </p>
            </div>
          </b-td>

          <!-- Payment Service Name -->
          <b-td
            class="debt-installment-bill-table__cell text-left tdProductName"
            role="cell"
            :aria-label="`Dịch vụ trả góp: ${item.paymentName}`"
          >
            <b>{{ item.paymentName }}</b>
          </b-td>

          <!-- Payment Code -->
          <b-td
            class="debt-installment-bill-table__cell text-right tdProductPrice"
            role="cell"
            :aria-label="`Mã hợp đồng: ${item.paymentCode}`"
          >
            {{ item.paymentCode }}
          </b-td>

          <!-- Payment Amount -->
          <b-td
            class="debt-installment-bill-table__cell text-right tdProductPrice"
            role="cell"
            :aria-label="`Tiền trả góp: ${formatCurrency(item.paymentAmount)}`"
          >
            {{ formatCurrency(item.paymentAmount) }}
          </b-td>

          <!-- Total Paid Amount -->
          <b-td
            :rowspan="bill.payments.length + 1"
            class="debt-installment-bill-table__cell debt-installment-bill-table__amount-paid text-right tdCenter"
            v-show="isShowRow(bill.payments, item)"
            role="cell"
            :aria-label="`Đã thanh toán: ${formatCurrency(bill.sumAmountTransactions)}`"
          >
            {{ formatCurrency(bill.sumAmountTransactions) }}
          </b-td>

          <!-- Remaining Amount -->
          <b-td
            :rowspan="bill.payments.length + 1"
            class="debt-installment-bill-table__cell debt-installment-bill-table__amount-remaining text-right tdCenter"
            v-show="isShowRow(bill.payments, item)"
            role="cell"
            :aria-label="`Còn lại: ${formatCurrency(bill.sumRemainTransaction)}`"
          >
            {{ formatCurrency(bill.sumRemainTransaction) }}
          </b-td>

          <!-- Total Bill Amount -->
          <b-td
            :rowspan="bill.payments.length + 1"
            class="debt-installment-bill-table__cell debt-installment-bill-table__amount-total text-right tdCenter"
            v-show="isShowRow(bill.payments, item)"
            role="cell"
            :aria-label="`Tổng tiền hóa đơn: ${formatCurrency(bill.totalAmount)}`"
          >
            {{ formatCurrency(bill.totalAmount) }}
          </b-td>

          <!-- Actions -->
          <b-td
            class="debt-installment-bill-table__cell debt-installment-bill-table__actions text-center"
            role="cell"
          >
            <b-dropdown
              size="sm"
              id="dropdown-left"
              right
              no-caret
              v-if="bill.sumRemainTransaction > 0"
              class="debt-installment-bill-table__action-dropdown"
            >
              <template slot="button-content">
                <i
                  class="flaticon2-settings debt-installment-bill-table__action-icon"
                  :aria-label="'Thao tác cho ' + item.paymentName"
                ></i>
              </template>

              <!-- Edit Payment -->
              <!-- <b-dropdown-item
                @click="$emit('edit-payment', bill)"
                class="debt-installment-bill-table__action-item"
              >
                <span class="debt-installment-bill-table__action-text">
                  <i class="flaticon2-pen debt-installment-bill-table__action-icon"></i>
                  &nbsp; Chỉnh sửa
                </span>
              </b-dropdown-item> -->

              <!-- Cash Payment -->
              <b-dropdown-item
                @click="$emit('cash-payment', { bill, item })"
                class="debt-installment-bill-table__action-item"
              >
                <span class="debt-installment-bill-table__action-text">
                  <i class="far fa-money-bill-alt debt-installment-bill-table__action-icon"></i>
                  &nbsp; Thanh toán bằng tiền mặt
                </span>
              </b-dropdown-item>

              <!-- Bank Transfer Payment -->
              <b-dropdown-item class="debt-installment-bill-table__action-item">
                <router-link
                  :to="getBankPaymentRoute(bill, item)"
                  class="debt-installment-bill-table__action-link"
                >
                  <span class="debt-installment-bill-table__action-text">
                    <i class="fas fa-money-check-alt debt-installment-bill-table__action-icon"></i>
                    &nbsp; Thanh toán bằng CK ngân hàng
                  </span>
                </router-link>
              </b-dropdown-item>
            </b-dropdown>
          </b-td>
        </b-tr>
      </b-tbody>

      <!-- Table Footer with Totals -->
      <b-tfoot>
        <tr role="row">
          <td
            colspan="5"
            class="debt-installment-bill-table__summary-label text-right"
            role="cell"
          >
            <b>Tổng</b>
          </td>
          <td
            class="debt-installment-bill-table__summary-value text-right"
            role="cell"
            :aria-label="`Tổng tiền trả góp: ${totals.totalPaymentAmount}`"
          >
            <b>{{ totals.totalPaymentAmount }}</b>
          </td>
          <td
            class="debt-installment-bill-table__summary-value text-right"
            role="cell"
            :aria-label="`Tổng đã thanh toán: ${totals.totalPaidAmount}`"
          >
            <b>{{ totals.totalPaidAmount }}</b>
          </td>
          <td
            class="debt-installment-bill-table__summary-value text-right"
            role="cell"
            :aria-label="`Tổng còn lại: ${totals.totalRemainingAmount}`"
          >
            <b>{{ totals.totalRemainingAmount }}</b>
          </td>
          <td
            class="debt-installment-bill-table__summary-value text-right"
            role="cell"
            :aria-label="`Tổng tiền hóa đơn: ${totals.totalBillAmount}`"
          >
            <b>{{ totals.totalBillAmount }}</b>
          </td>
          <td role="cell"></td>
        </tr>
      </b-tfoot>
    </b-table-simple>

    <!-- Loading Skeleton -->
    <vcl-table
      v-if="isLoading"
      :speed="5"
      :animate="true"
      :columns="11"
    />
  </div>
</template>

<script>
import { convertPrice } from '@/utils/common';
import { formatDate } from '@/utils/date';
import _ from 'lodash';
import { VclTable } from 'vue-content-loading';

export default {
  name: 'DebtInstallmentBillTable',
  components: {
    VclTable,
  },
  props: {
    /**
     * Table data array
     */
    data: {
      type: Array,
      default: () => [],
      validator: (data) => Array.isArray(data),
    },
    /**
     * Loading state
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Summary totals object
     */
    totals: {
      type: Object,
      default: () => ({
        totalPaymentAmount: '0',
        totalPaidAmount: '0',
        totalRemainingAmount: '0',
        totalBillAmount: '0',
      }),
    },
  },
  computed: {
    tableAriaLabel() {
      return 'Bảng dữ liệu công nợ trả góp hóa đơn theo ngày';
    },
  },
  methods: {
    /**
     * Check if row should be shown (for rowspan logic)
     */
    isShowRow(payments, currentItem) {
      return _.findIndex(payments, ['id', currentItem.id]) === 0;
    },

    /**
     * Format currency for display
     */
    formatCurrency(amount) {
      return convertPrice(amount);
    },

    /**
     * Format date for display
     */
    formatDate(dateString) {
      return formatDate(dateString);
    },

    /**
     * Get bank payment route configuration
     */
    getBankPaymentRoute(bill, item) {
      return {
        name: 'transaction',
        params: {
          mainModelProp: {
            contactType: '3',
            reconciliationDocType: '1',
            reconciliationDocId: bill.billNumber,
            type: '3',
            contactId: item.paymentRefId,
            contactName: item.paymentName,
            amount: item.paymentAmount,
            note: this.generatePaymentNote(bill, item),
          },
          mode: 'bank',
          action: 'add',
          id: 'new',
        },
      };
    },

    /**
     * Generate payment note
     */
    generatePaymentNote(bill, item) {
      let note = `Nhận thanh toán từ dịch vụ trả góp\n`;
      note += `Mã HĐ: ${bill.billNumber}\n`;
      note += `Dịch vụ: ${item.paymentName}\n`;
      note += `Mã hợp đồng: ${item.paymentCode || ''}`;
      return note;
    },
  },
};
</script>

<style lang="scss" scoped>
.debt-installment-bill-table {
  &__table {
    .table th,
    .table td {
      vertical-align: middle;
    }
  }

  &__header {
    font-weight: 600;
    color: #181c32;
  }

  &__customer-info {
    p {
      margin-bottom: 0;
    }
  }

  &__store-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__company-badge {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.35rem 0.65rem;
    letter-spacing: 0.2px;
    background-color: #0095e8;
    color: #fff;
    white-space: nowrap;
    line-height: 1;
  }

  &__amount-paid,
  &__amount-remaining,
  &__amount-total {
    color: #3c763d;
    font-weight: 700;
    width: 10%;
  }

  &__actions {
    width: 5%;
  }

  &__action-icon {
    font-size: 1rem !important;
    padding-right: 0px !important;
  }

  &__action-text {
    color: #3f4254;
    font-size: 12px;
  }

  &__action-link {
    color: #3f4254;
    text-decoration: none;

    &:hover {
      color: #3f4254;
      text-decoration: none;
    }
  }

  &__summary-label,
  &__summary-value {
    font-weight: bold;
  }
}
</style>
